import { useEffect, useState } from 'react';
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import api from 'utils/api';
import {
  EntityFormDialog,
  FormDialogHeader,
  FormDialogContent,
  FormDialogFooter,
  FormSection
} from 'components/form/EntityFormDialog';
import useFormValidation from 'hooks/useFormValidation';
import { validators } from 'utils/formValidation';
import { useToast } from 'contexts/ToastContext';

const PAYMENT_TYPES = [
  { value: 'card', label: 'Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'credit', label: 'Credit' },
  { value: 'online', label: 'Online' }
];

const emptyForm = () => ({
  name: '',
  code: '',
  type: ''
});

function normalizeType(raw) {
  const t = String(raw ?? '').toLowerCase().trim();
  return PAYMENT_TYPES.some((o) => o.value === t) ? t : '';
}

function methodToForm(method) {
  if (!method) return emptyForm();
  const type =
    method.type ?? method.payment_type ?? method.method_type ?? method.PaymentMethod?.type ?? '';
  return {
    name: method.name ?? method.name_en ?? method.method_name ?? '',
    code: method.code ?? method.method_code ?? method.slug ?? '',
    type: normalizeType(type)
  };
}

export default function PaymentMethodForm({
  open,
  paymentMethodId,
  initialMethod,
  onSave,
  onClose
}) {
  const isEdit = Boolean(paymentMethodId);
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const { formError, validate, setServerError, clearFieldError, fieldErrorProps, fieldErrors } =
    useFormValidation();

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm(methodToForm(initialMethod));
    } else {
      setForm(emptyForm());
    }
  }, [open, isEdit, initialMethod, paymentMethodId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name) clearFieldError(name);
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const ok = validate(
      {
        name: [validators.required('Name')],
        code: [validators.required('Code')],
        type: [validators.required('Type')]
      },
      form
    );
    if (!ok) return;

    const payload = {
      name: form.name.trim(),
      code: form.code.trim(),
      type: form.type.trim()
    };

    setSubmitting(true);
    try {
      const res = isEdit
        ? await api.put(`/payment/method/edit/${paymentMethodId}`, payload)
        : await api.post('/payment/method/add', payload);

      if (res.data?.status === false) {
        throw new Error(
          res.data?.message ||
            (isEdit ? 'Failed to update payment method' : 'Failed to add payment method')
        );
      }

      showToast(
        res.data?.message ||
          (isEdit ? 'Payment method updated' : 'Payment method added'),
        { severity: 'success' }
      );
      onSave();
      onClose();
    } catch (err) {
      setServerError(
        err.response?.data?.message ||
          err?.message ||
          (isEdit ? 'Failed to update payment method' : 'Failed to add payment method')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EntityFormDialog open={open} onClose={onClose} maxWidth="sm">
      <FormDialogHeader
        icon={<CreditCardRoundedIcon />}
        title={isEdit ? 'Edit Payment Method' : 'Add Payment Method'}
        onClose={onClose}
      />

      <FormDialogContent formError={formError}>
        <FormSection title="Payment method details">
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid size={{xs:12}}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  {...fieldErrorProps('name')}
                />
              </Grid>
              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Code"
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  placeholder="e.g. cash, card"
                  {...fieldErrorProps('code')}
                />
              </Grid>
              <Grid size={{xs:12, sm:6}}>
                <FormControl
                  fullWidth
                  required
                  size="small"
                  error={Boolean(fieldErrors.type)}
                >
                  <InputLabel id="payment-method-type-label" shrink>
                    Type
                  </InputLabel>
                  <Select
                    labelId="payment-method-type-label"
                    id="payment-method-type"
                    name="type"
                    label="Type"
                    value={form.type}
                    onChange={handleChange}
                    displayEmpty
                    notched
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>Select type</span>
                        );
                      }
                      return PAYMENT_TYPES.find((o) => o.value === value)?.label ?? value;
                    }}
                  >
                    {PAYMENT_TYPES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.type ? (
                    <FormHelperText>{fieldErrors.type}</FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>
            </Grid>
          </Stack>
        </FormSection>
      </FormDialogContent>

      <FormDialogFooter
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitLabel={isEdit ? 'Save changes' : 'Add payment method'}
      />
    </EntityFormDialog>
  );
}
